import { teamsHighContrastTheme, webDarkTheme, webLightTheme, type Theme } from '@fluentui/react-components'
import { useTheme, type ResolvedTheme } from '../theme'

/**
 * Fluent's stock dark theme paints its surfaces neutral grey (#292929…), which reads as a foreign
 * panel on Niobe's blue-black dark scheme. So the dark theme takes Niobe's own surface tokens for
 * every neutral background the widget's dialogs use. They are `var(--…)` references, not copied
 * hex values: Fluent writes theme values verbatim into CSS custom properties, and the tokens
 * resolve from `<html data-theme>`, so the dialogs track tokens.css with no second copy here.
 *
 * Fluent's neutral background ramp, mapped level by level: 1 is the raised surface (dialogs,
 * inputs), 2–5 and 7 the darker recessed levels, 6 and 8 the lighter accents. Every hover goes one
 * step lighter, every pressed/selected two. Text, strokes and brand colours stay Fluent's.
 */
const BASE: Record<string, string> = {
  '1': 'var(--surface)',
  '2': 'var(--bg)',
  '3': 'var(--bg)',
  '4': 'var(--bg)',
  '5': 'var(--bg)',
  '6': 'var(--surface-2)',
  '7': 'var(--bg)',
  '8': 'var(--raise)',
}

function niobeBackground(key: string): string | undefined {
  const m = /^colorNeutralBackground(\d)(Hover|Pressed|Selected)?$/.exec(key)
  if (!m) return undefined
  if (m[2] === 'Hover') return 'var(--surface-2)'
  if (m[2]) return 'var(--raise)'
  return BASE[m[1]!]
}

const niobeDarkTheme: Theme = Object.fromEntries(
  Object.entries(webDarkTheme).map(([k, v]) => [k, niobeBackground(k) ?? v]),
) as unknown as Theme

/**
 * The widget's dialogs are Fluent, and Niobe has no FluentProvider for them to inherit, so Niobe
 * hands the provider the Fluent theme matching the scheme it actually paints. `system` is already
 * resolved (and live) in `resolvedTheme` — no matchMedia of our own. Applies to the widget's
 * dialogs only, never to Niobe's UI.
 */
const FLUENT_THEMES: Record<ResolvedTheme, Theme> = {
  light: webLightTheme,
  dark: niobeDarkTheme,
  'high-contrast': teamsHighContrastTheme,
}

export function userhubThemeFor(resolved: ResolvedTheme): Theme {
  return FLUENT_THEMES[resolved]
}

export function useUserhubTheme(): Theme {
  return userhubThemeFor(useTheme().resolvedTheme)
}
